import { Component, OnInit } from '@angular/core';
import { AnalyticsService } from '../../services/analytics.service';
import { UsersService } from '../../services/users.service';
import { LeadService } from '../../services/lead.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss']
})
export class AnalyticsComponent implements OnInit {
  tabs = [
    { label: 'Leads', icon: 'fas fa-users' },
    { label: 'Communication', icon: 'fas fa-comments' }
  ];

  users: any = [];
  selectedUser: any = "";
  selectedYear: any = new Date().getFullYear().toString();
  years: any[] = ["2020", "2021", "2022", "2023", "2024", "2025"];
  isUserLoading: boolean = false;
  isAdmin: boolean = false;

  activeTabIndex = 0;
  leadLoading: boolean = false;
  leadsAnalytics: any = {};
  totalLeads: number = 0;
  pipelineStages: any[] = [];
  selectedPipelineId: number | null = null;
  isPipelineLoading: boolean = false;
  defaultPipelineStages: any[] = [];

  leadsCards: any[] = [];

  constructor(
    private localStorage: LocalStorageService,
    private api: AnalyticsService,
    private userApi: UsersService,
    private leadApi: LeadService
  ) {
    const role = this.localStorage.getRole();
    this.isAdmin = ['Administrator', 'Admin', 'admin', 'administrator'].includes(role);
  }

  ngOnInit() {
    this.loadPipelineStages();
    this.fnGetLeadAnalytics();
    this.getAllUsers();

    const currentYear = new Date().getFullYear();
    if (!this.years.includes(currentYear.toString())) {
      this.years.push(currentYear.toString());
    }
  }

  getAllUsers() {
    const param = {
      where: {
        hospital_id: this.localStorage.getHospitalId(),
        center_id: this.localStorage.getCenterId()
      },
      page: 0,
      limit: 1000,
      filter: null
    };
    this.isUserLoading = true;
    this.userApi.findAllUsers(param).subscribe({
      next: (res: any) => {
        const usersData = res?.rows || res || [];
        this.users = usersData.map((user: any) => {
          return {
            ...user,
            name: [user.fname, user.lname].join(' '),
            id: user.user_id
          };
        });

        if (this.isAdmin) {
          this.users.unshift({ name: 'All Users', id: '' });
        }
        this.isUserLoading = false;
      },
      error: (e: any) => {
        console.error(e);
        this.isUserLoading = false;
      },
    });
  }

  handleTabChange(index: number) {
    this.activeTabIndex = index;
  }

  fnChangeUser(event: any) {
    this.selectedUser = event;
    this.fnGetLeadAnalytics();
  }

  fnChangeYear(event: any) {
    this.selectedYear = event;
    this.fnGetLeadAnalytics();
  }

  fnGetLeadAnalytics() {
    const start_date = `${this.selectedYear}-01-01`;
    const end_date = `${this.selectedYear}-12-31`;

    let param: any = {
      hospital_id: this.localStorage.getHospitalId(),
      start_date,
      end_date
    };

    if (this.selectedUser && this.selectedUser !== "") {
      param.user_id = this.selectedUser;
    }

    this.leadLoading = false;
    this.api.getLeadAnalytics(param).subscribe({
      next: (res) => {
        if (res) {
          this.leadsAnalytics = res;
          this.leadLoading = true;
          this.totalLeads = res.total_leads;
          this.leadsCards = [
            {
              title: 'Total Leads',
              icon: 'fas fa-users',
              value: this.leadsAnalytics?.total_leads
            },
            {
              title: 'Raw Leads',
              icon: 'fas fa-database',
              value: this.leadsAnalytics?.raw_leads
            },
            {
              title: 'Qualified Leads',
              icon: 'fas fa-check-circle',
              value: this.leadsAnalytics?.qualified_leads
            },
            {
              title: 'Unqualified Leads',
              icon: 'fas fa-times-circle',
              value: this.leadsAnalytics?.unqualified_leads
            },
            {
              title: 'Working Leads',
              icon: 'fas fa-briefcase',
              value: this.leadsAnalytics?.working_leads
            },
            {
              title: 'Assigned Leads',
              icon: 'fas fa-user-tag',
              value: this.leadsAnalytics?.assigned_leads
            }
          ];

          if (this.defaultPipelineStages.length > 0) {
            this.updateLeadStatusFromPipeline(this.defaultPipelineStages);
          }
        }
      },
      error: (e) => {
        this.leadLoading = true;
        this.leadsAnalytics = {
          "platform_wise_leads": {
            "tina": { "Jan": 0, "Feb": 0, "Mar": 0, "Apr": 0, "May": 0, "Jun": 0, "Jul": 0, "Aug": 0, "Sep": 0, "Oct": 0, "Nov": 0, "Dec": 0 },
            "manual": { "Jan": 0, "Feb": 0, "Mar": 0, "Apr": 0, "May": 0, "Jun": 0, "Jul": 0, "Aug": 0, "Sep": 0, "Oct": 0, "Nov": 0, "Dec": 0 },
            "whatsapp": { "Jan": 0, "Feb": 0, "Mar": 0, "Apr": 0, "May": 0, "Jun": 0, "Jul": 0, "Aug": 0, "Sep": 0, "Oct": 0, "Nov": 0, "Dec": 0 }
          },
          "leads_types": {
            "Hot Leads": "0",
            "Cold Leads": "0",
            "Warm Leads": "0",
            "New Leads": "0"
          },
          "leads_status": {
            "Completed": "0",
            "Follow Up": "0",
            "Communicated": "0",
            "Pending": "0"
          },
          "total_leads": "0",
          "completed_leads": "0",
          "pending_leads": "0",
          "communicated_leads": "0",
          "followup_leads": "0"
        };
      }
    });
  }

  loadPipelineStages() {
    let param = {
      hospital_id: this.localStorage.getHospitalId(),
      pipeline_type: 'lead'
    };
    this.isPipelineLoading = true;
    this.leadApi.getPipelines(param).subscribe({
      next: (res: any) => {
        if (res && res.length > 0) {
          const defaultPipeline = res.find((pipeline: any) => pipeline.set_default === true);
          if (defaultPipeline) {
            this.selectedPipelineId = defaultPipeline.id;
            this.defaultPipelineStages = defaultPipeline.stages;
            if (this.leadsAnalytics && this.leadsAnalytics.leads_status) {
              this.updateLeadStatusFromPipeline(defaultPipeline.stages);
            }
          }
          this.pipelineStages = res.map((pipeline: any) => ({
            label: pipeline.pipeline_name,
            value: pipeline.id,
            stages: pipeline.stages
          }));
        }
        this.isPipelineLoading = false;
      },
      error: (e: any) => {
        console.error('Error loading pipeline stages:', e);
        this.isPipelineLoading = false;
      },
    });
  }

  onPipelineChange(event: any) {
    const selectedPipeline = this.pipelineStages.find(p => p.value === Number(event.target.value));
    if (selectedPipeline && selectedPipeline.stages) {
      this.updateLeadStatusFromPipeline(selectedPipeline.stages);
    }
  }

  updateLeadStatusFromPipeline(stages: any[]) {
    const newLeadsStatus: any = {};
    stages.forEach((stage: any) => {
      newLeadsStatus[stage.stage_name] = this.leadsAnalytics?.leads_status?.[stage.stage_name] || "0";
    });
    this.leadsAnalytics.leads_status = newLeadsStatus;
  }
}
